import { OffsetPercentType, PositionUtil } from "@uikit/util/FocusNodeUtil";
import { isEqual } from "lodash";
import { ReactFlowInstance, SetCenterOptions, Transform, Viewport, XYPosition, getTransformForBounds } from "reactflow";

export type MoveToOptions = SetCenterOptions & {
  /**
   * 偏移百分比
   *
   * - 如果值为 负数则表示减,相反则加
   */
  offsetPercent?: OffsetPercentType;

  /**
   * 该属性用于标识移动动画的唯一ID， 用于作重复动画的判断，防止在同样的环境下执行多次 setCenter
   *
   * 一般可能是节点ID，边的ID
   */
  moveId?: string;
};

export type MoveToStatusType = "skip" | "finished";
export type MoveToHandlerResult = {
  type: MoveToStatusType;
  options: ComposeResult;
};
type MoveToHandler = (position: XYPosition, options?: MoveToOptions) => Promise<MoveToHandlerResult>;

type ComposeArgs = MoveToOptions & Pick<ComposeResult, "position" | "viewport" | "container" | "transform">;
type ComposeResult = MoveToOptions & {
  /**
   * 真实移动的坐标（计算后）
   */
  position: XYPosition;

  /**
   * react flow 视口状态
   */
  viewport: Viewport;

  transform: Transform;

  /**
   * 上次移动的整个窗口大小
   */
  container: { width: number; height: number };
};

const DEFAULT_DURATION = 200;
const DEFAULT_ZOOM = 1;
class MoveToStore {
  static prevOptions?: ComposeResult;

  /**
   * 组合可对比参数
   * @param options
   * @returns
   */
  static compose = (options: ComposeArgs): ComposeResult => {
    let { offsetPercent, position } = options;

    // 计算百分比偏移
    position = PositionUtil.offsetPositionPercent({ position, offset: offsetPercent });
    let zoom = options.zoom ?? DEFAULT_ZOOM;
    if (zoom < 1) {
      zoom = DEFAULT_ZOOM;
    }
    return {
      ...options,
      duration: DEFAULT_DURATION,
      zoom: zoom,
      position,
    };
  };

  /**
   * 用来判断是否重复执行动画
   *
   * 根据上一次的对象所有值和现在的做一个对比(loadah.isEqual)
   *
   * @param options1
   * @param options2
   * @returns
   */
  static isSkip(target: ComposeResult) {
    return isEqual(target, MoveToStore.prevOptions);
  }

  static skip(options: ComposeResult) {
    return Promise.resolve({ type: "skip", options } as MoveToHandlerResult);
  }

  static finish(options: ComposeResult) {
    MoveToStore.prevOptions = options;

    return new Promise<MoveToHandlerResult>(resolve => {
      setTimeout(() => {
        resolve({ type: "finished", options: options });
      }, options.duration);
    });
  }
}

export function useSetCenter(reactFlow: ReactFlowInstance<any, any>) {
  /**
   * 移动到视口
   *
   * - 默认 duration:800, zomm:1
   * - 并返回 Promise
   *
   * @param options
   */
  const moveTo: MoveToHandler = (position, options) => {
    const transform = getTransformForBounds(
      {
        width: window.innerWidth,
        height: window.innerHeight,
        ...position,
      },
      window.innerWidth,
      window.innerHeight,
      0.1,
      2
    );
    const composeOptions = MoveToStore.compose({
      ...options,
      position,
      transform,
      viewport: reactFlow.getViewport(),
      container: { width: window.innerWidth, height: window.innerHeight },
    });

    // 是否跳过
    if (MoveToStore.isSkip(composeOptions)) {
      return MoveToStore.skip(composeOptions);
    }

    reactFlow.setCenter(position.x, position.y, composeOptions);

    return MoveToStore.finish(composeOptions);
  };

  const setCenter = (x: number, y: number, options?: MoveToOptions) => {
    return moveTo({ x, y }, options);
  };

  return {
    moveTo,
    setCenter,
  };
}
